import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, Download, Eye } from 'lucide-react';
import { useTasks } from '../../hooks/useTasks';
import { useNotifications } from '../NotificationSystem';
import './TaskHistoryDashboard.css';

const TaskHistoryDashboard = ({ userId }) => {
  const { t } = useTranslation('tasks');
  const { notify } = useNotifications();
  const [filters, setFilters] = useState({
    status: 'completed',
    searchTerm: '',
    startDate: '',
    endDate: ''
  });
  const [selectedTask, setSelectedTask] = useState(null);

  const { tasks, loading, error, refresh } = useTasks(filters);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return t('notSet');
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString();
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };

  const getTotalDuration = (task) => {
    let total = 0;
    task.protocols.forEach(protocol => {
      if (protocol.duration) total += protocol.duration;
    });
    return total;
  };

  const handleViewDetails = (task) => {
    setSelectedTask(task);
  };

  const handleCloseDetails = () => {
    setSelectedTask(null);
  };

  const handleExport = () => {
    notify.info(t('exportComingSoon'));
  };

  return (
    <div className="task-history-dashboard">
      <div className="dashboard-header">
        <div>
          <h2>{t('taskHistory')}</h2>
          <p className="subtitle">{t('taskHistorySubtitle')}</p>
        </div>
        <button className="btn btn-secondary" onClick={handleExport}>
          <Download size={16} /> {t('export')}
        </button>
      </div>

      <div className="history-filters">
        <input
          type="text"
          placeholder={t('searchTasksPlaceholder')}
          value={filters.searchTerm}
          onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
          className="search-input"
        />
        <input
          type="date"
          value={filters.startDate}
          onChange={(e) => handleFilterChange('startDate', e.target.value)}
          className="date-input"
          placeholder={t('startDate')}
        />
        <input
          type="date"
          value={filters.endDate}
          onChange={(e) => handleFilterChange('endDate', e.target.value)}
          className="date-input"
          placeholder={t('endDate')}
        />
        <select
          value={filters.status}
          onChange={(e) => handleFilterChange('status', e.target.value)}
          className="filter-select"
        >
          <option value="">{t('allStatusesFilter')}</option>
          <option value="completed">{t('completed')}</option>
          <option value="cancelled">{t('cancelled')}</option>
          <option value="failed">{t('failed')}</option>
        </select>
      </div>

      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>{t('loadingTaskHistory')}</p>
        </div>
      ) : error ? (
        <div className="error-state">
          <p>{t('errorLoading')}: {error}</p>
          <button onClick={refresh} className="btn btn-secondary">{t('retry')}</button>
        </div>
      ) : tasks.length === 0 ? (
        <div className="empty-state">
          <Calendar size={48} color="#9ca3af" />
          <p>{t('noTasksFound')}</p>
          <p className="empty-hint">{t('tryAdjustingFilters')}</p>
        </div>
      ) : (
        <div className="history-table">
          <div className="table-header">
            <div className="col-name">{t('taskName')}</div>
            <div className="col-executor">{t('executor')}</div>
            <div className="col-date">{t('completedAt')}</div>
            <div className="col-duration">{t('duration')}</div>
            <div className="col-compliance">{t('compliance')}</div>
            <div className="col-quality">{t('quality')}</div>
            <div className="col-actions">{t('actions')}</div>
          </div>

          {tasks.map(task => (
            <div key={task.id} className="table-row">
              <div className="col-name">
                <div className="task-name">{task.name}</div>
                <div className="task-type">{task.taskType}</div>
              </div>
              <div className="col-executor">{task.assignedToName || task.assignedTo || '—'}</div>
              <div className="col-date">{formatDateTime(task.actualEnd)}</div>
              <div className="col-duration">{formatDuration(getTotalDuration(task))}</div>
              <div className="col-compliance">
                {task.complianceScore != null ? (
                  <span className={`compliance-score ${task.complianceScore >= 90 ? 'high' : task.complianceScore >= 70 ? 'medium' : 'low'}`}>
                    {task.complianceScore}%
                  </span>
                ) : (
                  <span className="compliance-score">—</span>
                )}
              </div>
              <div className="col-quality">
                <span className={`quality-badge quality-${(task.completionQuality || '').toLowerCase()}`}>
                  {task.completionQuality || '—'}
                </span>
              </div>
              <div className="col-actions">
                <button
                  className="btn btn-info btn-icon"
                  onClick={() => handleViewDetails(task)}
                  title={t('viewDetails')}
                >
                  <Eye size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Task Details Modal */}
      {selectedTask && (
        <div className="modal-overlay" onClick={handleCloseDetails}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{selectedTask.name}</h3>
              <button className="btn btn-secondary" onClick={handleCloseDetails}>×</button>
            </div>

            <div className="modal-body">
              <div className="detail-section">
                <h4>{t('taskInformation')}</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="label">{t('executorLabel')}</span>
                    <span className="value">{selectedTask.assignedToName || selectedTask.assignedTo || '—'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">{t('completedAt')}:</span>
                    <span className="value">{formatDateTime(selectedTask.actualEnd)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Duration:</span>
                    <span className="value">{formatDuration(getTotalDuration(selectedTask))}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">{t('compliance')}:</span>
                    <span className="value">{selectedTask.complianceScore != null ? `${selectedTask.complianceScore}%` : '—'}</span>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h4>{t('protocolsExecuted')}</h4>
                {selectedTask.protocols.map((protocol, index) => (
                  <div key={index} className="protocol-detail">
                    <div className="protocol-detail-header">
                      <span className="protocol-name">{protocol.protocolName}</span>
                      <span className={`status-badge status-${protocol.status}`}>
                        {protocol.status}
                      </span>
                    </div>
                    <div className="protocol-steps">
                      {protocol.steps.map((step, stepIndex) => (
                        <div key={stepIndex} className={`step-detail ${step.isSkipped ? 'skipped' : ''}`}>
                          <span className="step-name">{step.stepName}</span>
                          <span className="step-status">
                            {step.isSkipped ? t('skipped') : t('completed')}
                            {step.duration && ` (${formatDuration(step.duration)})`}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {selectedTask.deviations && selectedTask.deviations.length > 0 && (
                <div className="detail-section">
                  <h4>Deviations</h4>
                  {selectedTask.deviations.map((deviation, index) => (
                    <div key={index} className="deviation-item">
                      <span className={`deviation-type ${deviation.impact.toLowerCase()}`}>
                        {deviation.type}
                      </span>
                      <span className="deviation-desc">{deviation.description}</span>
                    </div>
                  ))}
                </div>
              )}

              {selectedTask.notes && (
                <div className="detail-section">
                  <h4>{t('notes')}</h4>
                  <p className="notes-text">{selectedTask.notes}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskHistoryDashboard;


